import React, { useState, useEffect } from "react";
import { X, Save, Loader, Store, IndianRupee } from "lucide-react";
import { storeItemApi } from "../services/api";

/**
 * StoreItemPricingModal Component
 * Manages store-specific pricing and availability for an item
 */
const StoreItemPricingModal = ({ item, onClose, onSave }) => {
  const [storeItems, setStoreItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (item && item.id) {
      loadStoreItems();
    }
  }, [item]);

  const loadStoreItems = async () => {
    try {
      setLoading(true);
      setError("");
      
      // Fetch store-specific pricing for this item
      const response = await storeItemApi.getStoreItemsByItemId(item.id);
      
      if (response.success && response.data) {
        setStoreItems(response.data);
      }
    } catch (err) {
      console.error("Error loading store items:", err);
      setError("Failed to load store pricing data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStoreItemChange = (storeId, field, value) => {
    setStoreItems((prev) =>
      prev.map((si) =>
        si.store_id === storeId
          ? { ...si, [field]: field === "price" ? parseFloat(value) || 0 : value }
          : si,
      ),
    );
    setHasChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const storeItemsData = storeItems.map((si) => ({
        store_id: si.store_id,
        price: si.price,
        available: si.available,
      }));

      await storeItemApi.batchUpsertStoreItems(item.id, storeItemsData);
      
      console.log("✅ Store pricing saved successfully");
      
      if (onSave) {
        onSave();
      }
      
      onClose();
    } catch (err) {
      console.error("Error saving store pricing:", err);
      setError(err?.response?.data?.message || "Failed to save store pricing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200 bg-[#fffaf5]">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Store size={20} className="text-[#6b4226]" />
              <h2 className="text-xl font-bold text-gray-900">Store-Specific Pricing</h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              <strong>{item.name}</strong>
              {item.sku_code && <span className="ml-2 text-xs">SKU: {item.sku_code}</span>}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Base Price: ₹{item.price?.toFixed(2) || "0.00"}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="ml-4 text-gray-400 hover:text-gray-600 transition focus:outline-none focus:ring-2 focus:ring-[#6b4226] rounded-full p-1 disabled:opacity-50"
            title="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-[#6b4226]" size={32} />
            </div>
          ) : error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : storeItems.length === 0 ? (
            <div className="text-center py-12">
              <Store size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No store-specific pricing available yet.</p>
              <p className="text-xs text-gray-400 mt-2">
                Prices will be created during catalog sync.
              </p>
            </div>
          ) : (
            <>
              {/* Info Box */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Each store can have its own price and availability.
                </p>
              </div>

              {/* Store Pricing Table */}
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Store Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Branch Code</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">City</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">
                        <div className="flex items-center justify-end gap-1">
                          <IndianRupee size={14} />
                          Price (₹)
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Available</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {storeItems.map((storeItem) => {
                      const priceDiff = storeItem.price - (item.price || 0);
                      const isDifferent = Math.abs(priceDiff) > 0.01;

                      return (
                        <tr key={storeItem.store_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900 font-medium">
                            {storeItem.store?.store_name || "Unknown Store"}
                          </td>
                          <td className="px-4 py-3 text-gray-700 font-mono text-xs">
                            {storeItem.store?.branch_code || "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {storeItem.store?.city || "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={storeItem.price}
                                onChange={(e) =>
                                  handleStoreItemChange(storeItem.store_id, "price", e.target.value)
                                }
                                className="w-24 px-2 py-1.5 text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                              />
                              {isDifferent && (
                                <span
                                  className={`text-xs font-semibold ${
                                    priceDiff > 0 ? "text-red-600" : "text-green-600"
                                  }`}
                                  title={`${priceDiff > 0 ? "+" : ""}₹${priceDiff.toFixed(2)} from base`}
                                >
                                  {priceDiff > 0 ? "↑" : "↓"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={storeItem.available}
                              onChange={(e) =>
                                handleStoreItemChange(
                                  storeItem.store_id,
                                  "available",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            {storeItem.store?.is_active ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                Inactive
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Change Warning */}
              {hasChanges && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    ⚠️ <strong>Unsaved Changes:</strong> You have modified store pricing. Click "Save
                    Changes" to apply.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !hasChanges || storeItems.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#6b4226] text-white rounded-lg hover:bg-[#58341f] transition focus:outline-none focus:ring-2 focus:ring-[#6b4226] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader className="animate-spin" size={16} />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreItemPricingModal;

